// Служебные ручки сервера. Каталог и умолчания — только вошедшим, сведения о
// входе — всем. Все по относительному адресу: страница должна одинаково
// работать и по localhost, и через обратный прокси.

import type { SessionConfig } from "./protocol.ts";

/** Голос в каталоге сервера. */
export interface VoiceInfo {
  id: string;
  has_index: boolean;
}

export interface Catalog {
  voices: VoiceInfo[];
  has_silero_vad: boolean;
}

/** Границы, которые сервер применит к настройкам сессии. */
export interface SessionLimits {
  max_sample_rate: number;
  max_block_size: number;
  max_block_ms: number;
  max_extra_context_ms: number;
  max_crossfade_ms: number;
  max_sola_search_ms: number;
  max_pitch_shift: number;
  max_vad_release_ms: number;
  max_highpass_hz: number;
  max_input_gain_db: number;
  min_input_gain_db: number;
  max_gate_envelope_ms: number;
  min_gate_threshold_db: number;
  vad_modes: string[];
  /** Порог по умолчанию для каждого режима: шкалы у них разные. */
  vad_default_thresholds: Record<string, number>;
}

export interface Defaults {
  session: SessionConfig;
  limits: SessionLimits;
}

/** Сессии нет или она истекла. Отдельный тип: лечится входом, а не повтором. */
export class UnauthorizedError extends Error {
  constructor() {
    super("нужно войти");
    this.name = "UnauthorizedError";
  }
}

/**
 * Вошли, но доступа нет.
 *
 * Отдельно от `UnauthorizedError` намеренно: тот лечится повторным входом, а
 * этот не лечится ничем, что можно сделать в браузере. Слить их значило бы
 * гонять человека по кругу входов вместо того, чтобы отправить к тому, кто
 * раздаёт доступы.
 */
export class ForbiddenError extends Error {
  constructor() {
    super("нет доступа");
    this.name = "ForbiddenError";
  }
}

async function getJson<T>(path: string): Promise<T> {
  // Cookie сессии браузер приложит сам: адрес тот же, а `credentials` по
  // умолчанию — `same-origin`. Токенов в коде страницы нет вовсе.
  const response = await fetch(path, { cache: "no-store" });

  if (response.status === 401 || response.status === 403) {
    throw response.status === 403 ? new ForbiddenError() : new UnauthorizedError();
  }
  if (!response.ok) {
    throw new Error(`${path}: сервер ответил ${response.status}`);
  }
  return (await response.json()) as T;
}

export function fetchCatalog(): Promise<Catalog> {
  return getJson<Catalog>("/voices");
}

export function fetchDefaults(): Promise<Defaults> {
  return getJson<Defaults>("/defaults");
}

/** Кто вошёл — ответ открытой ручки `/auth/session`. */
export interface SessionInfo {
  /** `false` — сервер поднят с `--insecure-no-auth`, и вход не нужен. */
  enabled: boolean;
  signed_in: boolean;
  username?: string;
  /** У вошедшего без группы сессия тоже есть — чтобы сказать ему, кем он вошёл. */
  access?: "allowed" | "forbidden";
}

export async function fetchSession(): Promise<SessionInfo> {
  const response = await fetch("/auth/session", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`/auth/session: сервер ответил ${response.status}`);
  }
  return (await response.json()) as SessionInfo;
}
